"use client";

import { Menu } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";

import { DashboardLink } from "@/components/models/dashboard-link";
import { Button } from "@/lib/components/button";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/lib/components/sheet";
import { useAppConfig } from "@/lib/config";
import Logo from "@/lib/icons/Logo";
import { cn } from "@/lib/utils";

import type { User } from "better-auth";

interface MobileNavProps {
	user: User | null;
}

export function MobileNav({ user }: MobileNavProps) {
	const pathname = usePathname();
	const [open, setOpen] = useState(false);
	const { publicSignupAllowed } = useAppConfig();

	const links = [
		{ href: "/models", label: "Models" },
		{ href: "/ranking", label: "Rankings" },
		{ href: "/models/compare", label: "Compare" },
		{ href: "/providers", label: "Providers" },
		{ href: "https://docs.routerbench.com", label: "Docs", external: true },
	];

	return (
		<Sheet open={open} onOpenChange={setOpen}>
			<SheetTrigger asChild>
				<Button variant="ghost" size="icon" className="md:hidden">
					<Menu className="size-5" />
					<span className="sr-only">Toggle menu</span>
				</Button>
			</SheetTrigger>
			<SheetContent side="left" className="w-[300px] sm:w-[400px]">
				<SheetHeader className="border-b pb-4 mb-4">
					<SheetTitle className="text-left flex items-center gap-2">
						<Logo className="size-7" />
						<span>RouterBench</span>
					</SheetTitle>
				</SheetHeader>
				<nav className="flex flex-col gap-4">
					{links.map((link) => (
						<Link
							key={link.href}
							href={link.href as any}
							onClick={() => setOpen(false)}
							target={link.external ? "_blank" : undefined}
							rel={link.external ? "noopener noreferrer" : undefined}
							className={cn(
								"text-base font-medium transition-colors hover:text-primary px-2 py-1.5 rounded-md",
								pathname === link.href
									? "text-primary bg-muted"
									: "text-muted-foreground",
							)}
						>
							{link.label}
						</Link>
					))}
				</nav>
				<div className="mt-8 flex flex-col gap-4 border-t pt-4">
					{user ? (
						<div onClick={() => setOpen(false)}>
							<DashboardLink />
						</div>
					) : (
						<>
							<Button variant="ghost" asChild onClick={() => setOpen(false)}>
								<Link href="/login">Sign in</Link>
							</Button>
							{publicSignupAllowed && (
								<Button asChild onClick={() => setOpen(false)}>
									<Link href="/signup">Get Started</Link>
								</Button>
							)}
						</>
					)}
				</div>
			</SheetContent>
		</Sheet>
	);
}
